import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { MobileMenu } from "./MobileMenu/MobileMenu";
import { Logo } from "../../assets/images/Logo";
import { media } from "../../styles/mixins";
import { useTranslation } from "react-i18next";
import { COLORS, CONTAINER_WIDTHS } from "../../assets/constants/styles";
import { Button } from "../common/Button/Button";
import { openExtensionStore } from "../../helpers/various-helpers";
import { pxToRem } from "../../styles/helpers";

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { t } = useTranslation();

  const navItems = useMemo(
    () => [
      { title: t("navigation.about"), href: "#about" },
      { title: t("navigation.quickstart"), href: "#quickstart" },
      { title: t("navigation.benefits"), href: "#benefits" },
      { title: t("navigation.pricing"), href: "#pricing" },
    ],
    [t]
  );

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();

    const targetElement = document.querySelector(href);
    if (targetElement) {
      const targetPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = targetPosition + window.pageYOffset - 80;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    }
  };

  return (
    <>
      <HeaderWrapper>
        <HeaderContainer>
          <TitleContainer>
            <LogoContainer>
              <Logo />
            </LogoContainer>
            <Title>Grensa.AI</Title>
          </TitleContainer>
          <Navigation>
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
              >
                {item.title}
              </NavLink>
            ))}
          </Navigation>

          <ButtonsWrapper>
            <Button
              text={t("buttons.try")}
              onClick={() => {
                openExtensionStore();
              }}
            />
            <MenuButton onClick={toggleMobileMenu}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 12H21M3 6H21M3 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </MenuButton>
          </ButtonsWrapper>
        </HeaderContainer>
      </HeaderWrapper>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navItems={navItems}
        handleNavClick={handleNavClick}
      />
    </>
  );
};

const HeaderWrapper = styled.header`
  background: ${COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  position: fixed;
  top: 0;
  left: 0;
  padding: 1rem 0;
  z-index: 20;

  width: 100%;
  margin: 0 auto;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  max-width: ${pxToRem(CONTAINER_WIDTHS.mobile)};

  ${media.tablet(`
    max-width: ${pxToRem(CONTAINER_WIDTHS.tablet)};
    height: 2.813rem;
  `)}

  ${media.laptop(`
      max-width: ${pxToRem(CONTAINER_WIDTHS.laptop)};
  `)}


  ${media.desktop(`
    max-width: ${pxToRem(CONTAINER_WIDTHS.desktop)};
  `)}
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 27.32px;
  margin-left: 8px;
  color: ${COLORS.black};
`;

const LogoContainer = styled.div`
  width: 32px;
`;

const Navigation = styled.nav`
  display: none;

  ${media.tablet(`
    display: flex;
    gap: 12px;
  `)}

  ${media.laptop(`
    gap: 32px;
  `)}
`;

const NavLink = styled.a`
  color: ${COLORS.black};
  text-decoration: none;
  font-size: 16px;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;

  ${media.tablet(`
    display: none;
  `)}
`;
